import { useRef, useState, type KeyboardEvent } from 'react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { Num, T } from 'gt-react';
import { AGENT_RUN_CATEGORIES, type AgentRunCategory } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from 'src/lib/utils';
import {
  excludingCategories,
  withCategoryShown,
  type RunHistoryFilter,
} from '../agentRunHistoryFilter';
import {
  RUN_CATEGORY_GROUPS,
  RUN_CATEGORY_RULES,
  useRunCategoryCopy,
} from '../runCategories';

/** What the pointer or arrow keys rest on: the details panel explains it. */
function CategoryDetails({ category }: { category: AgentRunCategory }) {
  const copy = useRunCategoryCopy().categories[category];
  return (
    <div
      className="space-y-1 border-t border-border px-3 py-2 text-[11.5px]"
      aria-live="polite"
    >
      <p className="font-medium text-stone-900">{copy.label}</p>
      <p className="text-muted-foreground">{copy.description}</p>
      <p className="flex flex-wrap gap-1 pt-0.5">
        {RUN_CATEGORY_RULES[category].map((part) => (
          <code
            key={part}
            className="rounded bg-stone-100 px-1 py-0.5 font-mono text-[10.5px] text-stone-600"
          >
            {part}
          </code>
        ))}
      </p>
    </div>
  );
}

/**
 * Keeps Enter and Space on a header button: cmdk's root handles Enter itself
 * (it selects the highlighted row and cancels the key), which would otherwise
 * swallow the button's own click.
 */
function keepActivationKeys(event: KeyboardEvent<HTMLButtonElement>) {
  if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
}

/**
 * The History "Run Types" dropdown: one checkbox per run category, grouped
 * under Running / Succeeded / Didn't start / Failed, each with how many runs
 * it holds. A ticked box means those runs are shown. The panel at the bottom
 * explains whichever option is under the pointer or the arrow keys, so it
 * works without a mouse too.
 */
export function RunCategoryFilter({
  filter,
  onFilterChange,
  counts,
}: {
  filter: RunHistoryFilter;
  onFilterChange: (next: RunHistoryFilter) => void;
  /** Runs per category before the filter; a missing count is unknown. */
  counts: Partial<Record<AgentRunCategory, number>> | null;
}) {
  const copy = useRunCategoryCopy();
  const [highlighted, setHighlighted] = useState<string>('running');
  const listRef = useRef<HTMLDivElement>(null);
  const excluded = new Set(filter.excludeCategories);
  const shownCount = AGENT_RUN_CATEGORIES.length - excluded.size;
  const allShown = excluded.size === 0;
  const noneShown = shownCount === 0;
  const highlightedCategory = AGENT_RUN_CATEGORIES.find(
    (category) => category === highlighted,
  );

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-[11.5px]"
        >
          <T context="agent page history: button that opens the filter for which kinds of run are listed">
            Run Types
          </T>
          <span className="rounded-sm bg-stone-100 px-1 tabular-nums text-muted-foreground">
            {allShown ? (
              <T context="agent page history run-type filter: every kind of run is shown">
                All
              </T>
            ) : (
              <T context="agent page history run-type filter: how many kinds of run are shown out of all kinds">
                <Num>{shownCount}</Num> of{' '}
                <Num>{AGENT_RUN_CATEGORIES.length}</Num>
              </T>
            )}
          </span>
          <CaretDownIcon className="size-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(20rem,calc(100vw-2rem))] p-0"
        align="start"
        // Open with the list focused, so arrows and Enter act on the rows;
        // Tab reaches Select All / Clear All.
        initialFocus={listRef}
      >
        <Command
          ref={listRef}
          tabIndex={-1}
          className="outline-none"
          value={highlighted}
          onValueChange={setHighlighted}
          shouldFilter={false}
          loop
        >
          <div className="flex items-center gap-1 border-b border-border px-2 py-1.5">
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11.5px]"
              disabled={allShown}
              onKeyDown={keepActivationKeys}
              onClick={() =>
                onFilterChange(excludingCategories([], filter.range))
              }
            >
              <T context="agent page history run-type filter: tick every kind of run, so all runs are shown">
                Select All
              </T>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11.5px]"
              disabled={noneShown}
              onKeyDown={keepActivationKeys}
              onClick={() =>
                onFilterChange(
                  excludingCategories(AGENT_RUN_CATEGORIES, filter.range),
                )
              }
            >
              <T context="agent page history run-type filter: untick every kind of run, so no runs are shown">
                Clear All
              </T>
            </Button>
          </div>
          <CommandList className="max-h-[min(30rem,60vh)]">
            {RUN_CATEGORY_GROUPS.map((group, index) => (
              <div key={group.key}>
                {index > 0 && <CommandSeparator />}
                <CommandGroup heading={copy.groups[group.key]}>
                  {group.categories.map((category) => {
                    const shown = !excluded.has(category);
                    const count = counts?.[category];
                    return (
                      <CommandItem
                        key={category}
                        value={category}
                        aria-checked={shown}
                        onSelect={() =>
                          onFilterChange(
                            withCategoryShown(filter, category, !shown),
                          )
                        }
                        className="text-[12.5px]"
                      >
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded-[4px] border',
                            shown
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-input [&_svg]:invisible',
                          )}
                        >
                          <CheckIcon className="size-3" />
                        </span>
                        <span className="flex-1">
                          {copy.categories[category].label}
                        </span>
                        {count !== undefined && (
                          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                            {count}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </div>
            ))}
          </CommandList>
          {highlightedCategory && (
            <CategoryDetails category={highlightedCategory} />
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
